import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trophy, TrendingUp, Timer, Activity } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BaselineTesting = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vertical_jump: '',
    sprint_time: '',
    lane_agility: '',
    weight: '',
    body_fat_percent: '',
    core_hold_time: '',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        vertical_jump: parseFloat(formData.vertical_jump),
        sprint_time: parseFloat(formData.sprint_time),
        lane_agility: parseFloat(formData.lane_agility),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        body_fat_percent: formData.body_fat_percent ? parseFloat(formData.body_fat_percent) : null,
        core_hold_time: formData.core_hold_time ? parseFloat(formData.core_hold_time) : null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/baseline`, payload, { headers });
      
      toast.success('Baseline test completed! Your 16-week journey begins now.');
      
      // Update user in localStorage
      const userData = JSON.parse(localStorage.getItem('user'));
      userData.baseline_completed = true;
      userData.current_week = 1;
      userData.current_phase = 1;
      localStorage.setItem('user', JSON.stringify(userData));
      
      navigate('/program');
    } catch (error) {
      toast.error('Failed to save baseline test');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
            <Trophy className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            DAY 1: BASELINE TESTING
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Welcome to the <span className="text-primary font-bold">112-Day Transformation</span>. 
            Before we build your engine, we need to know where you're starting.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/30 rounded-sm">
            <span className="text-accent font-bold">16 WEEKS</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-white">4 PHASES</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-white">ELITE STATUS</span>
          </div>
        </div>

        {/* Test Battery Form */}
        <Card className="bg-card border-border" data-testid="baseline-form-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading font-bold text-white">Your Starting Numbers</CardTitle>
            <CardDescription>
              These metrics will be your baseline. We'll re-test every 4 weeks to track your transformation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Required Tests */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-bold text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Required Tests
                </h3>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vertical_jump">Vertical Jump (inches) *</Label>
                    <Input
                      id="vertical_jump"
                      data-testid="vertical-jump-input"
                      type="number"
                      step="0.5"
                      name="vertical_jump"
                      value={formData.vertical_jump}
                      onChange={handleChange}
                      placeholder="28.5"
                      required
                    />
                    <p className="text-xs text-muted-foreground">Standing or approach max</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sprint_time">3/4 Court Sprint (seconds) *</Label>
                    <Input
                      id="sprint_time"
                      data-testid="sprint-time-input"
                      type="number"
                      step="0.01"
                      name="sprint_time"
                      value={formData.sprint_time}
                      onChange={handleChange}
                      placeholder="3.45"
                      required
                    />
                    <p className="text-xs text-muted-foreground">Baseline to opposite FT line</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lane_agility">Lane Agility (seconds) *</Label>
                    <Input
                      id="lane_agility"
                      data-testid="lane-agility-input"
                      type="number"
                      step="0.01"
                      name="lane_agility"
                      value={formData.lane_agility}
                      onChange={handleChange}
                      placeholder="11.2"
                      required
                    />
                    <p className="text-xs text-muted-foreground">Complete lane drill pattern</p>
                  </div>
                </div>
              </div>

              {/* Optional Measurements */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-secondary" />
                  Optional Measurements
                </h3>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (lbs)</Label>
                    <Input
                      id="weight"
                      data-testid="weight-input"
                      type="number"
                      step="0.1"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="180"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="body_fat_percent">Body Fat %</Label>
                    <Input
                      id="body_fat_percent"
                      data-testid="bodyfat-input"
                      type="number"
                      step="0.1"
                      name="body_fat_percent"
                      value={formData.body_fat_percent}
                      onChange={handleChange}
                      placeholder="12.5"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="core_hold_time">Core Hold (seconds)</Label>
                    <Input
                      id="core_hold_time"
                      data-testid="core-hold-input"
                      type="number"
                      step="1"
                      name="core_hold_time"
                      value={formData.core_hold_time}
                      onChange={handleChange}
                      placeholder="120"
                    />
                    <p className="text-xs text-muted-foreground">Plank hold max time</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="How are you feeling? Any injuries or concerns?"
                  rows={3}
                />
              </div>

              {/* Submit */}
              <div className="flex justify-center pt-4">
                <Button
                  data-testid="submit-baseline-button"
                  type="submit"
                  className="bg-primary hover:bg-primary/90 text-white font-bold uppercase px-12 py-6 text-lg"
                  disabled={loading}
                >
                  {loading ? 'Starting Your Journey...' : 'Begin 16-Week Program'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* What's Next */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30">
          <CardContent className="p-6">
            <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
              <Timer className="w-5 h-5 text-primary" />
              What Happens Next?
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <div className="text-sm font-bold text-accent">WEEKS 1-4</div>
                <div className="text-sm text-gray-300">Structural Integrity</div>
                <div className="text-xs text-muted-foreground">Build the chassis</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-accent">WEEKS 5-8</div>
                <div className="text-sm text-gray-300">Force Production</div>
                <div className="text-xs text-muted-foreground">Increase power ceiling</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-accent">WEEKS 9-12</div>
                <div className="text-sm text-gray-300">Explosive Power</div>
                <div className="text-xs text-muted-foreground">Light the fuse</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-accent">WEEKS 13-16</div>
                <div className="text-sm text-gray-300">Game Conditioning</div>
                <div className="text-xs text-muted-foreground">4th quarter beast</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BaselineTesting;